import React, { useState, useEffect, useCallback } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getFirestore, doc, onSnapshot } from "firebase/firestore";
import { PortfolioGridItem } from "./PorfolioGridItem";
import { normalizeQuote, unrealizedGain, totalReturnPct, totalsByCurrency } from "../../lib/portfolioMetrics";
import { convertToBase } from "../../lib/fx";
import { cachedFetch, TTL } from "../../lib/clientCache";

const COLUMNS = [
  { key: "ticker",      label: "Ticker | Exchange" },
  { key: "price",       label: "Price"             },
  { key: "quantity",    label: "Quantity"           },
  { key: "value",       label: "Value"              },
  { key: "capitalGain", label: "Capital Gain (Unrealised)" },
  { key: "income",      label: "Income"             },
  { key: "totalReturn", label: "Total Return (Unrealised)" },
  { key: "currency",    label: "Currency"           },
  { key: "return",      label: "Return (Unrealised)" },
];

function sortIndicator(columnKey, sortConfig) {
  if (sortConfig.key !== columnKey) return " ↕";
  return sortConfig.direction === "asc" ? " ↑" : " ↓";
}

// Builds display rows. When an `fx` rates object ({ base, rates }) is given,
// value / cost base / capital gain / income are converted into the base
// currency (falling back to the instrument currency when a rate is missing).
// Capital gains in base currency use the account-currency cost (cash debited
// at the historical FX rate) where available, so they include currency gains.
export function buildRows(data, priceData, dividendData = {}, metricsByTicker = {}, fx = null) {
  return (Object.entries(data) || []).map(([, stockEntry]) => {
    const ticker  = Object.keys(stockEntry)[0];
    const details = stockEntry[ticker];
    const exchange = details?.exchange || "-";

    let quantity = 0;
    if (details?.netQuantity !== null && details?.netQuantity !== undefined) {
      quantity = details.netQuantity;
    } else {
      try {
        (details?.holdings || []).forEach((entry) => {
          Object.values(entry).forEach((val) => {
            if (Array.isArray(val)) quantity += val[0] || 0;
          });
        });
      } catch (_) { /* malformed entry */ }
    }

    const priceEntry = priceData[ticker];
    const { price, currency } = priceEntry
      ? normalizeQuote(priceEntry.price ?? null, priceEntry.currency ?? "-")
      : { price: null, currency: "-" };
    const valueLocal = price !== null ? price * quantity : null;

    const metrics = metricsByTicker[ticker];

    // Income: actual broker-reported dividends first (account currency),
    // falling back to the Yahoo-derived estimate (instrument currency).
    const divEntries = dividendData?.[ticker]?.exDates ?? [];
    const yahooIncome = divEntries.length > 0
      ? parseFloat(divEntries.filter((e) => !e.isFuture).reduce((sum, e) => sum + e.totalGross, 0).toFixed(4))
      : null;
    const txIncome       = metrics?.incomeNet ?? null;
    const incomeLocal    = txIncome !== null ? txIncome : yahooIncome;
    const incomeCurrency = txIncome !== null ? metrics?.accountCurrency : currency;

    const costLocal       = metrics?.costBasis ?? null;
    const capitalGainLocal = metrics ? unrealizedGain(valueLocal, costLocal) : null;

    // Base-currency conversions; each falls back to the local figure when no
    // rate is available so the grid degrades gracefully.
    const valueBase = fx ? convertToBase(valueLocal, currency, fx) : null;
    const costBase = fx && metrics
      ? (metrics.costBasisAccount !== null && metrics.costBasisAccount !== undefined
          ? convertToBase(metrics.costBasisAccount, metrics.accountCurrency, fx)
          : convertToBase(costLocal, metrics.currency || currency, fx))
      : null;
    const incomeBase = fx ? convertToBase(incomeLocal, incomeCurrency, fx) : null;

    const value       = valueBase ?? valueLocal;
    const costBasis   = costBase ?? costLocal;
    const unrealized  = valueBase !== null && costBase !== null
      ? valueBase - costBase
      : capitalGainLocal;
    const income = incomeBase ?? incomeLocal;

    // Realized gains: account-currency figure (cash in minus cash out) when
    // available, otherwise the instrument-currency FIFO gain.
    const realizedLocal = metrics?.realizedGain ?? null;
    const realizedBase = fx && metrics
      ? (metrics.realizedGainAccount !== null && metrics.realizedGainAccount !== undefined
          ? convertToBase(metrics.realizedGainAccount, metrics.accountCurrency, fx)
          : convertToBase(realizedLocal, metrics.currency || currency, fx))
      : null;
    const realized = realizedBase ?? realizedLocal;

    // Capital gain column = total gain (realized + unrealized), with the
    // unrealized component carried separately for the parenthesised display.
    const capitalGain = unrealized !== null
      ? unrealized + (realized || 0)
      : (realized !== null && realized !== 0 ? realized : null);

    // Return denominator: open cost base, falling back to total invested for
    // fully sold positions so realized returns still show.
    const buyCostBase = fx && metrics ? convertToBase(metrics.buyCost, metrics.currency || currency, fx) : null;
    const costForReturn = costBasis && costBasis > 0
      ? costBasis
      : (buyCostBase ?? metrics?.buyCost ?? null);

    // Total return value = capital gain + income (each component already in
    // the display currency); unrealised variant uses the unrealised gain.
    const totalReturn = capitalGain === null && income === null
      ? null
      : (capitalGain || 0) + (income || 0);
    const totalReturnUnrealized = unrealized === null && income === null
      ? null
      : (unrealized || 0) + (income || 0);

    return {
      ticker,
      exchange,
      price,
      quantity,
      value,
      costBasis,
      capitalGain,
      capitalGainUnrealized: unrealized,
      capitalGainLocal,
      realizedGain: realized,
      income,
      totalReturn,
      totalReturnUnrealized,
      currency,
      valueCurrency: valueBase !== null ? fx.base : currency,
      return: metrics ? totalReturnPct(capitalGain, income, costForReturn) : null,
      returnUnrealized: metrics ? totalReturnPct(unrealized, income, costForReturn) : null,
    };
  });
}

export function compareRows(a, b, { key, direction }) {
  let aVal = key === "ticker" ? `${a.ticker} ${a.exchange}` : a[key];
  let bVal = key === "ticker" ? `${b.ticker} ${b.exchange}` : b[key];

  const aNull = aVal === null || aVal === "-";
  const bNull = bVal === null || bVal === "-";
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;

  const cmp = typeof aVal === "string" ? aVal.localeCompare(bVal) : aVal - bVal;
  return direction === "asc" ? cmp : -cmp;
}

export function PortfolioGrid({ metrics = {}, fx = null, onRowsChange }) {
  const [data,         setData]         = useState(null);
  const [priceData,    setPriceData]    = useState({});
  const [dividendData, setDividendData] = useState({});
  const [sortConfig,   setSortConfig]   = useState({ key: null, direction: "asc" });

  const fetchPortfolio = useCallback(async () => {
    try {
      const portfolio = await cachedFetch("portfolio", TTL.PORTFOLIO, async () => {
        const result = await httpsCallable(getFunctions(), "getPortfolio")();
        return JSON.parse(result.data);
      });
      setData(portfolio);
    } catch (err) {
      console.error("Failed to fetch portfolio:", err);
    }
  }, []);

  useEffect(() => { fetchPortfolio(); }, [fetchPortfolio]);

  useEffect(() => {
    if (!data) return;
    const stockEntries = Object.entries(data) || [];
    const tickers = stockEntries.map(([, v]) => Object.keys(v)[0]);
    if (!tickers.length) return;

    const fns = getFunctions();
    const pricesKey = `prices:${[...tickers].sort().join(",")}`;

    // Prices: served from the client cache when fresh (skipping both the
    // function call and the Firestore listeners); only complete results
    // (every ticker priced) are cached.
    let unsubscribers = [];
    cachedFetch(
      pricesKey,
      TTL.PRICES,
      async () => {
        // Real-time listeners: update priceData per-ticker as the backend
        // writes each cache entry, while the call below resolves stale ones.
        const db = getFirestore();
        unsubscribers = tickers.map((ticker) =>
          onSnapshot(doc(db, "priceCache", ticker), (snapshot) => {
            if (snapshot.exists()) {
              setPriceData((prev) => ({ ...prev, [ticker]: snapshot.data() }));
            }
          })
        );
        const result = await httpsCallable(fns, "getPrices")({ query: tickers.join(",") });
        return JSON.parse(result.data);
      },
      (prices) => tickers.every((t) => prices?.[t]?.price !== null && prices?.[t]?.price !== undefined)
    )
      .then((prices) => setPriceData((prev) => ({ ...prev, ...prices })))
      .catch((err) => console.error("Price refresh error:", err));

    // Dividend history for all tickers, also client-cached.
    const tickerObjects = stockEntries.map(([, v]) => {
      const ticker = Object.keys(v)[0];
      return { ticker, exchange: v[ticker]?.exchange || "" };
    });
    cachedFetch("dividends", TTL.DIVIDENDS, async () => {
      const res = await httpsCallable(fns, "getDividendHistory")({ tickers: tickerObjects });
      return JSON.parse(res.data);
    })
      .then((divs) => setDividendData(divs))
      .catch((err) => console.error("Dividend history error:", err));

    return () => unsubscribers.forEach((unsub) => unsub());
  }, [data]);

  function handleHeaderClick(columnKey) {
    setSortConfig((prev) => ({
      key:       columnKey,
      direction: prev.key === columnKey && prev.direction === "asc" ? "desc" : "asc",
    }));
  }

  const rows   = data ? buildRows(data, priceData, dividendData, metrics, fx) : [];
  const sorted = sortConfig.key
    ? [...rows].sort((a, b) => compareRows(a, b, sortConfig))
    : rows;

  // Bubble computed rows up so the summary cards and diversity chart can
  // share the same data without duplicating fetch logic.
  useEffect(() => {
    if (onRowsChange && data) onRowsChange(buildRows(data, priceData, dividendData, metrics, fx));
  }, [onRowsChange, data, priceData, dividendData, metrics, fx]);

  // Totals group by the currency each row's value is expressed in; with FX
  // rates loaded this collapses to a single base-currency row.
  const valueTotals       = totalsByCurrency(rows, "value", "valueCurrency");
  const gainTotals        = totalsByCurrency(rows, "capitalGain", "valueCurrency");
  const unrealizedTotals  = totalsByCurrency(rows, "capitalGainUnrealized", "valueCurrency");
  const incomeTotals      = totalsByCurrency(rows, "income", "valueCurrency");
  const totalReturnTotals = totalsByCurrency(rows, "totalReturn", "valueCurrency");
  const trUnrealTotals    = totalsByCurrency(rows, "totalReturnUnrealized", "valueCurrency");
  const totalCurrencies   = Object.keys(valueTotals);

  function fmtTotalWithUnrealized(totals, unrealizedTotalsMap, cur) {
    const total = totals[cur];
    if (total === undefined) return "-";
    const unrealized = unrealizedTotalsMap[cur];
    if (unrealized === undefined || Math.abs(total - unrealized) < 0.005) return total.toFixed(2);
    return `${total.toFixed(2)} (${unrealized.toFixed(2)})`;
  }

  return (
    <table className="table">
      <thead>
        <tr>
          {COLUMNS.map((col) => (
            <th
              key={col.key}
              scope="col"
              onClick={() => handleHeaderClick(col.key)}
              style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
              data-testid={`col-header-${col.key}`}
            >
              {col.label}{sortIndicator(col.key, sortConfig)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sorted.map((row) => (
          <PortfolioGridItem key={row.ticker} row={row} />
        ))}
      </tbody>
      {totalCurrencies.length > 0 && (
        <tfoot>
          {totalCurrencies.map((cur) => (
            <tr key={cur} className="table-light fw-bold" data-testid={`totals-row-${cur}`}>
              <th scope="row">Total ({cur})</th>
              <td></td>
              <td></td>
              <td>{valueTotals[cur].toFixed(2)}</td>
              <td>{fmtTotalWithUnrealized(gainTotals, unrealizedTotals, cur)}</td>
              <td>{incomeTotals[cur] !== undefined ? incomeTotals[cur].toFixed(2) : "-"}</td>
              <td>{fmtTotalWithUnrealized(totalReturnTotals, trUnrealTotals, cur)}</td>
              <td>{cur}</td>
              <td></td>
            </tr>
          ))}
        </tfoot>
      )}
    </table>
  );
}
